import { CurrencyPipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, computed, inject, input, signal, viewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DateTime } from 'luxon';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { IdentitiesService } from '../../../service/identitites.service';
import { NotifyService } from '../../../service/notify.service';
import { PanoptycDataService } from '../../../service/panoptyc-data.service';
import { Branch, IdentityTopLevelRow, LocationTiny, SharedTransactionDetails, TransactionByFaceId } from '../../../types';

@Component({
  selector: 'app-identities',
  templateUrl: './identities.component.html',
  styleUrls: ['./identities.component.scss'],
  standalone: false,
})
export class IdentitiesComponent implements AfterViewInit {
  private notifyService = inject(NotifyService);
  private dataService = inject(PanoptycDataService);
  private identitiesService = inject(IdentitiesService);
  private currencyPipe = inject(CurrencyPipe);

  public selectedLocations = signal<LocationTiny[]>([]);
  public numberOfResults = signal<number>(0);
  public currentPage = signal<number>(0);
  public itemsPerPage = signal<number>(50);

  public branchId = input.required<number>();
  public branchData = input<Branch>();

  filterBoxExpanded = signal<boolean>(false);
  highlightApply = signal<boolean>(false);
  loadingData = signal<boolean>(false);
  suspectIdentities = signal<IdentityTopLevelRow[]>([]);
  selectedImageUrl = signal<string>('');
  loadingTransactionDetails = signal<boolean>(false);
  showTransaction = signal<TransactionByFaceId | undefined>(undefined);
  transactionDetails = signal<SharedTransactionDetails | undefined>(undefined);

  theftRisk = signal<'all' | 'high' | 'medium' | 'low'>('all');
  readonly dateRangeMaximumDays = 30;

  public readonly today = DateTime.now().startOf('day').toJSDate();
  public readonly defaultFromDate = DateTime.now().startOf('day').minus({ days: this.dateRangeMaximumDays }).toJSDate();
  dateFrom = linkedQueryParam<Date>('date_from', {
    parse: (value) => {
      if (value) {
        const parsed = DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate();
        return parsed;
      }
      return this.defaultFromDate;
    },
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : ''),
  });
  dateTo = linkedQueryParam<Date>('date_to', {
    parse: (value) => {
      if (value) {
        const parsed = DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate();
        return parsed;
      }
      return this.today;
    },
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : ''),
  });

  readonly paginator = viewChild(MatPaginator);
  readonly sort = viewChild(MatSort);
  readonly transactionDetailsModal = viewChild<PanModalComponent>('transactionDetailsModal');

  readonly displayedColumns: string[] = ['num_activities', 'cancel_date', 'img_id', 'total', 'location_name', 'branch'];
  readonly detailColumns: string[] = [
    'img_id',
    'cancel_date',
    'total',
    'theft',
    'location_name',
    'user_name',
    'branch',
    'badges',
    'theft_category',
  ];

  transactionsPerIdentity = {};

  public visibleIdentities = computed(() => {
    return this.suspectIdentities();
  });

  handleFiltersChanged() {
    this.highlightApply.set(true);
  }

  togglePanel() {
    this.filterBoxExpanded.set(!this.filterBoxExpanded());
  }

  loadTransactionPopup(row: TransactionByFaceId) {
    this.showTransaction.set(row);
    this.transactionDetailsModal().show();

    this.loadingTransactionDetails.set(true);

    this.dataService
      .getTransactionDetails(row.image_id)
      .pipe(
        finalize(() => {
          this.loadingTransactionDetails.set(false);
        }),
      )
      .subscribe((response) => {
        this.transactionDetails.set(response);
        this.dataService.setTransactionViewed(row.image_id).subscribe(() => {});
      });
  }

  handleTheftCategoryChange(event: { isTheft: boolean }) {
    const row = this.showTransaction();
    if (!row) {
      return;
    }

    const backingData = this.transactionsPerIdentity[row.face_id].data.find((d) => d.image_id === row.image_id);
    if (backingData) {
      backingData.is_theft = event.isTheft;
      backingData.theftLabel = this.getTheftLabel(event.isTheft);
    }
  }

  ngAfterViewInit() {
    this.sort().sortChange.subscribe(() => {
      this.paginator().pageIndex = 0;
      this.currentPage.set(0);
      this.resetLoadedChunk();
      this.refresh();
    });
    this.paginator().page.subscribe((event) => {
      this.currentPage.set(event.pageIndex);
      this.refresh();
    });

    this.refresh();
  }

  getSuspectIdentityIndex(faceId: string) {
    return this.suspectIdentities().findIndex((s) => s.face_id === faceId);
  }

  closeAllRecords() {
    for (const faceId in this.transactionsPerIdentity) {
      if (this.transactionsPerIdentity[faceId].haveData) {
        this.transactionsPerIdentity[faceId].data.forEach((d) => {
          if (d.timerSubscription) {
            clearInterval(d.timerSubscription);
          }
        });
        this.transactionsPerIdentity[faceId] = {};
        this.transactionsPerIdentity[faceId].haveData = false;
      }
    }
  }

  getRecord(row: IdentityTopLevelRow) {
    this.transactionsPerIdentity[row.face_id] ??= {};

    if (this.transactionsPerIdentity[row.face_id] && this.transactionsPerIdentity[row.face_id].haveData) {
      this.transactionsPerIdentity[row.face_id].data.forEach((d) => {
        if (d.timerSubscription) {
          clearInterval(d.timerSubscription);
        }
      });
      this.transactionsPerIdentity[row.face_id].haveData = false;
    } else {
      this.transactionsPerIdentity[row.face_id] = {};
      this.transactionsPerIdentity[row.face_id].loadingData = true;
      const params = this.getExtraParams(row.location_id).append('faceid', row.face_id);

      this.dataService.getTransactionsForIdentity(params).subscribe(
        (data) => {
          if (!data) {
            this.transactionsPerIdentity[row.face_id].loadingData = false;
            return;
          }

          const transactions = data.transactions.items;
          this.transactionsPerIdentity[row.face_id].data = transactions;
          this.transactionsPerIdentity[row.face_id].numEntries = transactions.length;
          transactions.forEach((d) => (d.theftLabel = this.getTheftLabel(d.is_theft)));
          this.transactionsPerIdentity[row.face_id].loadingData = false;
          this.transactionsPerIdentity[row.face_id].haveData = transactions.length > 0;

          const identityIdx = this.getSuspectIdentityIndex(row.face_id);

          this.suspectIdentities.update((identities) => {
            if (identityIdx === -1) {
              return identities;
            }

            const identity = identities[identityIdx];
            identity.num_activities = data.transactions.total_items;
            identity.all_activities = data.total_count;
            identity.total = data.total?.toFixed(2) ?? identity.total.toString();
            identity.running_total = data.running_total.toFixed(2);
            identity.theft_category = data.theft_category;

            return identities;
          });

          if (transactions.length === 0) {
            this.refresh(this.getExtraParams(row.location_id));
          }
        },
        () => {
          this.transactionsPerIdentity[row.face_id].loadingData = false;
        },
      );
    }
  }

  theftToValue(isTheft) {
    if (isTheft === true) {
      return 1;
    }
    if (isTheft === false) {
      return 0;
    }
    return isTheft;
  }

  getTheftLabel(isTheft) {
    if (isTheft === true || isTheft === 1) {
      return 'Incident';
    } else if (isTheft === false || isTheft === 0) {
      return 'No incident';
    } else {
      return 'Click to review';
    }
  }

  handleTheftChange(isTheft, row: TransactionByFaceId & { updatingCategory: boolean }) {
    const newCategory = isTheft === true ? 1 : 0;
    row.updatingCategory = true;

    this.dataService.updateTransactionCategory(row.image_id, newCategory).subscribe(() => {
      row.theftLabel = isTheft === true ? 'Incident' : 'No incident';
      row.is_theft = this.theftToValue(isTheft);
      row.updatingCategory = false;
      this.notifyService.notify('Successfully updated category');
    });
  }

  getExtraParams(locationId: number = null): HttpParams {
    const fromDate = this.dateFrom() ?? this.defaultFromDate;
    const toDate = this.dateTo() ?? this.today;
    let params = new HttpParams();

    if (locationId) {
      params = params.set('location_ids', locationId);
    }

    if (this.selectedLocations().length > 0) {
      this.selectedLocations().forEach((location) => {
        params = params.append('location_ids', location.id);
      });
    }

    params = params.set('cancel_date_from', `${DateTime.fromJSDate(fromDate).toFormat('yyyy-MM-dd')} 00:00:00`);
    params = params.set('cancel_date_to', `${DateTime.fromJSDate(toDate).toFormat('yyyy-MM-dd')} 23:59:59`);

    params = params.set('theft_risk', this.theftRisk());

    return params;
  }

  getIdentities() {
    const paginator = this.paginator();
    paginator.pageIndex = 0;
    this.currentPage.set(0);
    this.highlightApply.set(false);
    this.resetLoadedChunk();
    this.refresh();
  }

  handleRiskChange() {
    if (this.loadingData()) {
      return false;
    }

    this.closeAllRecords();
    this.getIdentities();
  }

  resetFilters() {
    this.dateFrom.set(this.defaultFromDate);
    this.dateTo.set(this.today);

    this.selectedLocations.set([]);
    this.highlightApply.set(true);
  }

  public getShowActivitiesButtonText(element: IdentityTopLevelRow) {
    const leadText =
      this.transactionsPerIdentity[element.face_id] && this.transactionsPerIdentity[element.face_id].haveData ? 'Hide' : 'Show';
    const numText =
      element.num_activities === element.all_activities
        ? `${element.all_activities} activities`
        : `${element.num_activities} (of ${element.all_activities})`;
    return `${leadText} ${numText}`;
  }

  public getTotalColumnText(element: IdentityTopLevelRow) {
    const totalText =
      element.total === element.running_total
        ? `${this.currencyPipe.transform(element.running_total)}`
        : `${this.currencyPipe.transform(element.total)} (of ${this.currencyPipe.transform(element.running_total)})`;
    return `${totalText}`;
  }

  private refresh(params = this.getExtraParams()): void {
    const pageIndex = this.currentPage();
    const hasCustomParams = params.toString() !== this.getExtraParams().toString();
    const absoluteStart = pageIndex * this.itemsPerPage();
    const isOutOfRange = this.numberOfResults() > 0 && absoluteStart >= this.numberOfResults();
    if (!hasCustomParams && isOutOfRange) {
      return;
    }

    const requestPageSize = this.itemsPerPage();
    this.loadingData.set(true);

    // get branchId somehow in B&L
    this.identitiesService
      .getIdentities(this.branchId(), this.sort().active, this.sort().direction, pageIndex, requestPageSize, params)
      .pipe(
        finalize(() => {
          this.loadingData.set(false);
        }),
      )
      .subscribe(
        (data) => {
          this.suspectIdentities.set(data.results);
          this.numberOfResults.set(data.count);
        },
        (error) => {
          this.notifyService.notify(tryGetErrorMessage(error));
        },
      );
  }

  private resetLoadedChunk(): void {
    this.suspectIdentities.set([]);
    this.numberOfResults.set(0);
  }
}
